import { Component, type ErrorInfo, type ReactNode } from "react";
import { AlertTriangle } from "lucide-react";

interface Props {
	children: ReactNode;
}

interface State {
	hasError: boolean;
	error: Error | null;
}

export class ErrorBoundary extends Component<Props, State> {
	state: State = { hasError: false, error: null };

	static getDerivedStateFromError(error: Error): State {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, info: ErrorInfo) {
		console.error("[ErrorBoundary]", error, info.componentStack);
	}

	render() {
		if (!this.state.hasError) return this.props.children;

		return (
			<div className="min-h-screen bg-warm-bg flex items-center justify-center p-6">
				<div className="bg-coral-light rounded-xl border border-coral/20 p-8 max-w-md w-full text-center space-y-4">
					<AlertTriangle className="w-12 h-12 text-coral mx-auto" />
					<h1 className="text-xl font-semibold text-coral-dark">
						Something went wrong
					</h1>
					<p className="text-sm text-coral-dark/70">
						{this.state.error?.message || "An unexpected error occurred."}
					</p>
					<div className="flex flex-col gap-2 pt-2">
						<button
							onClick={() => window.location.reload()}
							className="bg-coral text-white px-4 py-2 rounded-lg hover:bg-coral-hover transition-colors font-medium"
						>
							Reload page
						</button>
						<a
							href="/"
							className="text-sm text-coral-dark hover:text-coral transition-colors"
						>
							Go to dashboard
						</a>
					</div>
				</div>
			</div>
		);
	}
}

export default ErrorBoundary;
